import { useEffect, useState } from "react";
import api from "@/api/client";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import CardDetailPanel from "@/components/board/CardDetailPanel";
import { deriveInitials } from "@/lib/pipeline";
import { ownerColor, stageColor } from "@/lib/palette";
import { Pause, AlertTriangle } from "lucide-react";

export default function StuckPage() {
  const [days, setDays] = useState(5);
  const [cards, setCards] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [lists, setLists] = useState({});
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/reports/stuck`, { params: { days } }),
      api.get("/templates"),
      api.get("/users"),
      api.get("/lists"),
    ]).then(([s, t, u, l]) => { setCards(s.data); setTemplates(t.data); setUsers(u.data); setLists(l.data); });
  }, [days]);

  const tpl = (n) => templates.find((t) => t.stage_number === n);

  return (
    <div className="min-h-screen paper-bg">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-1">
          <Pause className="h-6 w-6 text-[#FBBF24]" />
          <h1 className="text-3xl font-extrabold tracking-tight text-[#15233B]">Stuck cards</h1>
        </div>
        <p className="text-sm text-[#15233B]/60">Active cards with no activity for ≥ {days} days. Sorted by stalest first.</p>
        <div className="mt-4 flex items-center gap-3 mb-4">
          <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#15233B]/55">Threshold</label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="stuck-days" className="border border-[#15233B]/15 rounded px-2 py-1.5 text-xs font-semibold bg-white">
            {[3, 5, 7, 10, 14, 21].map((n) => <option key={n} value={n}>{n}+ days</option>)}
          </select>
          <span className="text-xs font-bold text-[#15233B]/60">{cards.length} stuck</span>
        </div>
        <div className="bg-white border border-[#15233B]/10 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F1F2F4] text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">
              <tr><th className="text-left px-4 py-3">Client</th><th className="text-left px-4 py-3">Stage</th><th className="text-left px-4 py-3">Owner</th><th className="text-left px-4 py-3">Days idle</th><th className="text-left px-4 py-3">Last activity</th></tr>
            </thead>
            <tbody className="divide-y divide-[#15233B]/8">
              {cards.map((c) => {
                const t = tpl(c.current_stage);
                const sc = stageColor(c.current_stage);
                const oc = ownerColor(c.owner_id);
                const owner = users.find((u) => u.id === c.owner_id);
                return (
                  <tr key={c.id} onClick={() => setSelected(c)} data-testid={`stuck-row-${c.av_id}`} className="hover:bg-[#F6F3EC]/60 cursor-pointer">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-[#F1F2F4] grid place-items-center text-[11px] font-bold text-[#15233B]/70">{deriveInitials(c.full_name)}</div>
                        <div><div className="font-bold text-[#15233B]">{c.full_name}</div><div className="text-[11px] font-mono text-[#15233B]/55">{c.av_id}</div></div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: sc.primary, color: sc.text }}>{String(c.current_stage).padStart(2,"0")} {t?.name}</span></td>
                    <td className="px-4 py-3 text-xs"><span className="inline-flex items-center gap-1.5"><span className="h-5 w-5 rounded-full grid place-items-center text-[9px] font-bold" style={{ backgroundColor: oc.primary, color: oc.text }}>{(owner?.name||"?")[0]}</span>{owner?.name || "—"}</span></td>
                    <td className="px-4 py-3"><span className="text-xs font-bold px-2 py-0.5 rounded bg-[#b91c1c]/10 text-[#b91c1c]"><AlertTriangle className="inline h-3 w-3 mr-0.5" />{c.days_since_activity}d idle</span></td>
                    <td className="px-4 py-3 text-xs text-[#15233B]/60">{new Date(c.last_activity_at).toLocaleString()}</td>
                  </tr>
                );
              })}
              {cards.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-xs italic text-[#15233B]/40">No stuck cards 🎉</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {selected && (
        <CardDetailPanel card={selected} template={tpl(selected.current_stage)} templates={templates} users={users} lists={lists}
          onClose={() => setSelected(null)} onChanged={(u) => { setCards((p) => p.map((c) => c.id === u.id ? u : c)); setSelected(u); }} />
      )}
      <BrandFooter />
    </div>
  );
}
